import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await register(formData.email, formData.password, formData.name);
      toast.success("Account created successfully!");
      // Small delay to ensure state updates
      setTimeout(() => {
        navigate("/configurator");
      }, 100);
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-4 bg-[#0A0A0A]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-2">
            Create Account
          </h1>
          <p className="text-[#F4F1DE]/60">
            Join ReptiCraft and start building your dream enclosure
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
          <div>
            <Label htmlFor="name" className="text-[#F4F1DE]/80">Full Name</Label>
            <div className="relative mt-2">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F4F1DE]/40" />
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="pl-10 bg-[#121212] border-white/10 text-[#F4F1DE] h-12"
                placeholder="John Doe"
                required
                data-testid="register-name"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="text-[#F4F1DE]/80">Email</Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F4F1DE]/40" />
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="pl-10 bg-[#121212] border-white/10 text-[#F4F1DE] h-12"
                placeholder="you@example.com"
                required
                data-testid="register-email"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-[#F4F1DE]/80">Password</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F4F1DE]/40" />
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="pl-10 bg-[#121212] border-white/10 text-[#F4F1DE] h-12"
                placeholder="••••••••"
                required
                data-testid="register-password"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="text-[#F4F1DE]/80">Confirm Password</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F4F1DE]/40" />
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="pl-10 bg-[#121212] border-white/10 text-[#F4F1DE] h-12"
                placeholder="••••••••"
                required
                data-testid="register-confirm-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-6 font-bold uppercase tracking-widest btn-glow"
            data-testid="register-submit"
          >
            {loading ? (
              <span className="spinner" />
            ) : (
              <>
                Create Account
                <ArrowRight className="ml-2 w-5 h-5" />
              </>
            )}
          </Button>
        </form>

        <p className="text-center mt-6 text-[#F4F1DE]/60">
          Already have an account?{" "}
          <Link to="/login" className="text-[#E07A5F] hover:underline" data-testid="login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
